import React, { Fragment, ReactNode } from 'react';
import clsx from 'clsx';
import { Button, Gutter, Icon } from '@common/components';
import {
  Colors,
  ColorsType,
  FontSize,
  FontWeight,
  Spacing,
  ZIndex,
} from '@common/tokens';
import { WeekPill } from './_week-pill';

/** A column of the matrix (e.g. a weekday). The first column is a fixed title stub, not a column. */
export type PlannerCalendarColumnType = {
  key: string;
  header: ReactNode;
  /** Visually emphasize the whole column (e.g. weekends). */
  highlighted?: boolean;
};

/** A data row inside a section. `cells` must align 1:1 with `columns`. */
export type PlannerCalendarRowType = {
  key: string;
  title: ReactNode;
  cells: ReactNode[];
};

/**
 * A group of rows. Rendered as a header row (title + optional `accentColor` left border),
 * the data rows, and an optional closing `footer` row (e.g. a totals row).
 */
export type PlannerCalendarSectionType = {
  key: string;
  title: ReactNode;
  /** Color token applied as the left-border accent of the section header row. */
  accentColor?: ColorsType;
  rows: PlannerCalendarRowType[];
  footer?: {
    title: ReactNode;
    cells: ReactNode[];
    cellBackgrounds?: (ColorsType | undefined)[];
  };
};

/** A week in the navigation strip. Decoration is agnostic — the caller owns its meaning. */
export type PlannerCalendarWeekType = {
  key: string;
  label: ReactNode;
  /** Slot rendered inside the pill (e.g. a status dot or check). */
  indicator?: ReactNode;
  /** Coral-border emphasis on the pill (caller decides what it signifies, e.g. a flagged week). */
  alert?: boolean;
};

/**
 * A business-agnostic calendar matrix: a full-width table whose first row and first column are
 * sticky, with rows grouped into accent-bordered sections and optional closing rows. An optional
 * week-pill strip (with prev/next window navigation) sits on top, and a free `header` slot renders
 * between the strip and the grid.
 *
 * It is intentionally free of any domain concern — feed it columns, sections, and weeks. See
 * Storybook for usage; consumers (e.g. the scheduling grid) map their data onto these shapes.
 */
export function PlannerCalendar({
  columns,
  sections,
  weeks,
  selectedWeekKey,
  onWeekSelect,
  onPreviousWeeks,
  onNextWeeks,
  header,
  className,
}: {
  columns: PlannerCalendarColumnType[];
  sections: PlannerCalendarSectionType[];
  weeks?: PlannerCalendarWeekType[];
  selectedWeekKey?: string;
  onWeekSelect?: (key: string) => void;
  onPreviousWeeks?: () => void;
  onNextWeeks?: () => void;
  header?: ReactNode;
  className?: string;
}) {
  const hasControls = (weeks && weeks.length > 0) || !!header;

  return (
    <div className={clsx('wk-planner-calendar', className)}>
      {hasControls ? (
        <div className="wk-planner-calendar__controls">
          {weeks && weeks.length > 0 ? (
            <div className="wk-planner-calendar__week-strip">
              <Button
                kind="link"
                className="wk-planner-calendar__week-nav"
                onClick={onPreviousWeeks}
                disabled={!onPreviousWeeks}
                ariaLabel="Previous weeks"
                icon={<Icon icon="chevronLeft" kind="solid" />}
              />
              <div className="wk-planner-calendar__weeks">
                {weeks.map((week) => (
                  <WeekPill
                    key={week.key}
                    label={week.label}
                    indicator={week.indicator}
                    alert={week.alert}
                    selected={week.key === selectedWeekKey}
                    onClick={
                      onWeekSelect ? () => onWeekSelect(week.key) : undefined
                    }
                  />
                ))}
              </div>
              <Button
                kind="link"
                className="wk-planner-calendar__week-nav"
                onClick={onNextWeeks}
                disabled={!onNextWeeks}
                ariaLabel="Next weeks"
                icon={<Icon icon="chevronRight" kind="solid" />}
              />
            </div>
          ) : null}

          {header ? (
            <div className="wk-planner-calendar__header">{header}</div>
          ) : null}
        </div>
      ) : null}

      <div className="wk-planner-calendar__scroll">
        <table className="wk-planner-calendar__table">
          <thead>
            <tr>
              <th className="wk-planner-calendar__corner" />
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={clsx('wk-planner-calendar__col-header', {
                    'wk-planner-calendar__cell--highlighted':
                      column.highlighted,
                  })}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => {
              const footer = section.footer;
              return (
                <Fragment key={section.key}>
                  <tr className="wk-planner-calendar__section-header">
                    <td
                      className="wk-planner-calendar__stub wk-planner-calendar__stub--section"
                      style={
                        section.accentColor
                          ? {
                              // Accent stripe + the stub's right separator drawn as inset
                              // shadows so they ride with the sticky first column. A real
                              // border-left would belong to the collapsed-border table grid
                              // and scroll away with the cells.
                              boxShadow: `inset 5px 0 0 0 ${Colors[section.accentColor]}, inset -1px 0 0 0 ${Colors.steel}`,
                            }
                          : undefined
                      }
                    >
                      {section.title}
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={clsx('wk-planner-calendar__section-fill', {
                          'wk-planner-calendar__cell--highlighted':
                            column.highlighted,
                        })}
                      />
                    ))}
                  </tr>
                  {section.rows.map((row) => (
                    <tr key={row.key} className="wk-planner-calendar__row">
                      <td className="wk-planner-calendar__stub">{row.title}</td>
                      {columns.map((column, index) => (
                        <td
                          key={column.key}
                          className={clsx('wk-planner-calendar__cell', {
                            'wk-planner-calendar__cell--highlighted':
                              column.highlighted,
                          })}
                        >
                          <Gutter asPadding size="sm">
                            {row.cells[index]}
                          </Gutter>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {footer ? (
                    <tr className="wk-planner-calendar__footer">
                      <td className="wk-planner-calendar__stub wk-planner-calendar__stub--footer">
                        {footer.title}
                      </td>
                      {columns.map((column, index) => (
                        <td
                          key={column.key}
                          className={clsx(
                            'wk-planner-calendar__cell',
                            'wk-planner-calendar__cell--footer',
                            {
                              'wk-planner-calendar__cell--highlighted':
                                column.highlighted,
                            }
                          )}
                          style={handleBackgroundStyle(
                            footer?.cellBackgrounds?.[index]
                          )}
                        >
                          <Gutter asPadding size="sm">
                            {footer.cells[index]}
                          </Gutter>
                        </td>
                      ))}
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <style jsx>{`
        .wk-planner-calendar {
          display: flex;
          flex-direction: column;
          width: 100%;
          min-height: 0;
        }
        .wk-planner-calendar__controls {
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          padding: ${Spacing.lg}px ${Spacing.md}px ${Spacing.md}px;
          background: ${Colors.white};
          border-bottom: 1px solid ${Colors.steelLighter};
        }
        .wk-planner-calendar__week-strip {
          display: flex;
          align-items: center;
          gap: ${Spacing.xs}px;
        }
        .wk-planner-calendar__weeks {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: ${Spacing.xs}px;
        }
        .wk-planner-calendar__week-nav :global(.wk-icon) {
          color: ${Colors.midnightRegular};
        }
        .wk-planner-calendar__header {
          margin-top: ${Spacing.md}px;
        }
        .wk-planner-calendar__scroll {
          overflow: auto;
          flex: 1;
          min-height: 0;
        }
        /*
         * border-collapse: collapse with a border-bottom on each row and a border-right on each
         * cell (the grid prototype's approach). Sticky cells (stub column + header row) additionally
         * carry inset box-shadows so their separators paint above the sticky background instead of
         * being covered by it.
         */
        .wk-planner-calendar__table {
          border-collapse: collapse;
          width: 100%;
        }
        .wk-planner-calendar__col-header {
          position: sticky;
          top: 0;
          z-index: ${ZIndex.sticky};
          min-width: 160px;
          padding: ${Spacing.sm}px ${Spacing.lg / 2}px;
          text-align: center;
          white-space: nowrap;
          background: ${Colors.white};
          /* Single separators via inset shadow (survives the sticky background); no
             border, to avoid a doubled line against the body grid. */
          box-shadow:
            inset 0 -1px 0 0 ${Colors.steel},
            inset -1px 0 0 0 ${Colors.steel};
        }
        .wk-planner-calendar__corner {
          position: sticky;
          top: 0;
          left: 0;
          z-index: ${ZIndex.header};
          width: 1px;
          background: ${Colors.white};
          box-shadow: inset -1px -1px 0 0 ${Colors.steel};
        }
        .wk-planner-calendar__stub {
          position: sticky;
          left: 0;
          z-index: ${ZIndex.raised};
          width: 1px;
          white-space: nowrap;
          vertical-align: top;
          padding: ${Spacing.lg / 2}px ${Spacing.md}px;
          background: ${Colors.white};
          /* Only the right separator as an inset shadow (survives the sticky background).
             The horizontal line comes from the row's border-bottom — adding a bottom
             shadow here would double it. */
          box-shadow: inset -1px 0 0 0 ${Colors.steel};
        }
        .wk-planner-calendar__stub--section {
          background: ${Colors.white};
        }
        .wk-planner-calendar__stub--footer {
          font-size: ${FontSize.xs};
          font-weight: ${FontWeight.medium};
          color: ${Colors.midnightLighter};
          text-transform: uppercase;
          letter-spacing: 0.3px;
          background: ${Colors.steelLightest};
        }
        .wk-planner-calendar__col-header.wk-planner-calendar__cell--highlighted,
        .wk-planner-calendar__cell--highlighted {
          background: ${Colors.steelLightest};
        }
        .wk-planner-calendar__row {
          border-bottom: 1px solid ${Colors.steel};
        }
        .wk-planner-calendar__footer {
          border-bottom: 1px solid ${Colors.steel};
        }
        .wk-planner-calendar__cell {
          vertical-align: top;
          /* Inner spacing is owned by the consumer (e.g. a Gutter), so cells stay flush. */
          padding: 0;
          background: ${Colors.white};
          /* Vertical separator drawn as an inset shadow — border-right can lose the
             border-collapse conflict and drop out, but the shadow always paints. */
          box-shadow: inset -1px 0 0 0 ${Colors.steel};
        }
        .wk-planner-calendar__cell--footer {
          padding: 0;
          background: ${Colors.steelLightest};
          vertical-align: middle;
        }
        .wk-planner-calendar__section-header td {
          border-bottom: 1px solid ${Colors.steel};
        }
        .wk-planner-calendar__section-fill {
          background: ${Colors.white};
          box-shadow: inset -1px 0 0 0 ${Colors.steel};
        }
      `}</style>
    </div>
  );
}

function handleBackgroundStyle(
  color: ColorsType | undefined
): { background: string } | undefined {
  if (!color) return undefined;

  return { background: Colors[color] };
}
